import { Descriptions, Header, Space, Text } from '@procurify/ui'

import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { CostSummary } from '@webapp/core/components/CostSummary'
import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { Price } from '@webapp/core/components/Price'
import { UserLabel } from '@webapp/core/components/UserLabel'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  ExpenseItemTable,
  type ExpenseViewerRoleEnum,
} from '@webapp/modules/expense/components/ExpenseItemTable'
import { ExpenseStatusLabel } from '@webapp/modules/expense/components/ExpenseStatusLabel'
import { type Expense } from '@webapp/modules/expense/entities/Expense'
import messages from './messages'

export interface IExpenseSummaryProps {
  expense: Expense
  showCostSummary?: boolean
  showHeader?: boolean
  viewerRole: ExpenseViewerRoleEnum
}

export const ExpenseSummary = ({
  expense,
  showCostSummary = true,
  showHeader = true,
  viewerRole,
}: IExpenseSummaryProps) => {
  const intl = useProcurifyIntl()
  const formatDate = useFormatDate()

  return (
    <Space size='xlarge'>
      <Space>
        {showHeader && (
          <Space direction='horizontal' align='center' flexWrap>
            <Header as='h2'>
              {intl.formatMessage(messages.reportHeaderTitle, {
                id: expense.id,
                title: expense.title,
              })}
            </Header>
            <ExpenseStatusLabel size='medium' status={expense.status} />
          </Space>
        )}

        <Space direction='horizontal' size='large'>
          <Space.Item width={1 / 2}>
            <Space>
              <Header as='h5'>
                {intl.formatMessage(messages.requestSummaryHeader)}
              </Header>
              <Descriptions columns={1}>
                <Descriptions.Item
                  label={intl.formatMessage(messages.requestedByLabel)}
                >
                  <UserLabel user={expense.requester} />
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage(messages.locationLabel)}
                >
                  {expense.primaryCategory.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage(messages.departmentLabel)}
                >
                  {expense.secondaryCategory.name}
                </Descriptions.Item>

                {expense.hasTravel && expense.travel !== null && (
                  <Descriptions.Item
                    label={intl.formatMessage(messages.travelTicketLabel)}
                  >
                    <BridgeLink to={`/#/travels/${expense.travel.uuid}`}>
                      {intl.formatMessage(messages.travelTicketValue, {
                        id: expense.travel.id,
                        purpose: expense.travel.purpose,
                      })}
                    </BridgeLink>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Space>
          </Space.Item>

          <Space.Item width={1 / 2}>
            <Space>
              <Header as='h5'>
                {intl.formatMessage(messages.approvalSummaryHeader)}
              </Header>
              <Descriptions columns={1}>
                <Descriptions.Item
                  label={intl.formatMessage(messages.submittedLabel)}
                >
                  {formatDate(expense.submittedDate, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage(messages.lastModifiedLabel)}
                >
                  {formatDate(expense.dateLastModified, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Descriptions.Item>

                {expense.nextApprover !== null && (
                  <Descriptions.Item
                    label={intl.formatMessage(messages.nextApproverLabel)}
                  >
                    <UserLabel user={expense.nextApprover} showDelegatee />
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Space>
          </Space.Item>
        </Space>
      </Space>

      <Space>
        <Header as='h5'>
          {intl.formatMessage(messages.descriptionHeading)}
        </Header>
        <Text>
          {expense.hasDescription
            ? expense.description
            : intl.formatMessage(messages.noPurposeSpecified)}
        </Text>
      </Space>

      <ExpenseItemTable items={expense.items} viewerRole={viewerRole} />

      {showCostSummary !== false && (
        <Space direction='vertical' align='end'>
          <CostSummary>
            <CostSummary.Cost>
              <th id='cost-summary-subtotal'>
                {intl.formatMessage(messages.subtotalLabel)}
              </th>
              <td>
                <Price
                  price={expense.subtotal}
                  currency={expense.currency.name}
                  fontSize={14}
                  aria-labelledby='cost-summary-subtotal'
                />
              </td>
            </CostSummary.Cost>
            <CostSummary.Cost>
              <th id='cost-summary-tax'>
                {intl.formatMessage(messages.taxTotalLabel)}
              </th>
              <td>
                <Price
                  price={expense.taxTotal}
                  currency={expense.currency.name}
                  fontSize={14}
                  aria-labelledby='cost-summary-tax'
                />
              </td>
            </CostSummary.Cost>
            <CostSummary.Total>
              <th id='cost-summary-total'>
                {intl.formatMessage(messages.totalLabel)}
              </th>
              <td>
                <Price
                  price={expense.totalAmount}
                  currency={expense.currency.name}
                  fontSize={18}
                  aria-labelledby='cost-summary-total'
                />
              </td>
            </CostSummary.Total>
            <CostSummary.Cost>
              <th id='cost-summary-reimbursable'>
                {intl.formatMessage(messages.reimbursableLabel)}
              </th>
              <td>
                <Price
                  price={expense.reimbursableAmount}
                  currency={expense.currency.name}
                  fontSize={14}
                  aria-labelledby='cost-summary-reimbursable'
                />
              </td>
            </CostSummary.Cost>
          </CostSummary>
        </Space>
      )}
    </Space>
  )
}

ExpenseSummary.displayName = 'ExpenseSummary'
